import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, GitPullRequest, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const iconFor = (type) => (type === "revision_decided" ? CheckCircle2 : GitPullRequest);

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const items = data?.items || [];
  const unread = data?.unread_count || 0;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    setOpen(false);
    if (n.meta?.project_id) navigate(`/projects/${n.meta.project_id}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-secondary transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-unread-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel"
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-auto rounded-xl border border-border bg-popover shadow-2xl z-50 animate-fade-up">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-popover">
            <span className="font-bold text-sm">Notifications</span>
            {unread > 0 && (
              <button data-testid="notification-mark-all" onClick={markAll}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" />
              You're all caught up.
            </div>
          ) : (
            items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left border-b border-border/60 last:border-0 hover:bg-secondary transition-colors ${n.read ? "" : "bg-primary/5"}`}
                >
                  <span className="mt-0.5 h-7 w-7 shrink-0 grid place-items-center rounded-lg bg-primary/15 text-primary"><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{n.title}</span>
                      {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                    </span>
                    <span className="block text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</span>
                    <span className="block text-[10px] text-muted-foreground/70 mt-1 font-mono">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
